/**
 * AdhocProposalMessage — the chat "draft content" proposal, rendered
 * IN THE CHAT THREAD as Asthra's response message (tail block) while
 * the draft awaits the writer's Approve/Reject.
 *
 * A "draft" is a FRAGMENT to be inserted at the writer's cursor, so the
 * card previews only the new text and names where it will land. Only a
 * "redraft" (the /regen command) proposes a whole replacement body.
 *
 * Thin adapter over ``ProposalCard`` — THE shared text-edit proposal
 * surface (also behind EditProposalMessage and WriteApprovalMessage).
 * This module only maps an ad-hoc draft proposal onto the card:
 *   - "Draft content" intent chip + header naming section+instruction
 *   - sanitized markdown preview (exactly what Approve will store in
 *     the SSOT and insert into Word — the approve path runs the same
 *     sanitizer once and submits that string to both)
 *   - drift warning after a version-conflict 409 (the writer edited
 *     the section after the draft was made; approving again is a
 *     deliberate overwrite)
 *
 * SRP: pure presentation. State machine + apply logic live in
 * useAdhocProposalFlow; on decision the shell appends the durable
 * ``adhoc_draft`` EventLog entry (this transient component then
 * unmounts and the projected message takes its place in history).
 *
 * While the proposal waits on the writer (phase "pending_decision") an
 * ``AwaitingDecisionFrame`` status line sits under the card.
 */

import type React from "react";
import { useMemo } from "react";
import type { AdhocProposal, AdhocProposalPhase } from "../hooks/useAdhocProposalFlow";
import { intentLabel } from "../intentLabels";
import { sanitizeDraftForWord } from "../services/draftSanitizer";
import ApplyProgress, { type ApplyStep } from "./ApplyProgress";
import AwaitingDecisionFrame from "./AwaitingDecisionFrame";
import ProposalCard from "./ProposalCard";

interface AdhocProposalMessageProps {
  proposal: AdhocProposal;
  phase: AdhocProposalPhase;
  driftWarning: string | null;
  /** Post-approve apply checklist from useAdhocProposalFlow — non-empty
   *  only while an approve is being applied (empty for reject). */
  applySteps: ApplyStep[];
  onApprove: () => void;
  onReject: () => void;
}

export const AdhocProposalMessage: React.FC<AdhocProposalMessageProps> = ({
  proposal,
  phase,
  driftWarning,
  applySteps,
  onApprove,
  onReject,
}) => {
  const instructionPreview =
    proposal.instruction.length > 80 ? `${proposal.instruction.slice(0, 79)}…` : proposal.instruction;
  const isRedraft = proposal.mode === "redraft";
  // "Redraft" is a literal: /regen is a command, not a routed intent,
  // so it has no intentLabel entry anymore.
  const chip = isRedraft ? "Redraft" : intentLabel("draft_content");
  const noun = isRedraft ? "redraft" : "addition";
  const sectionLabel = proposal.sectionNumber ? `§${proposal.sectionNumber}` : proposal.sectionLabel;
  // Name the insertion point: the writer chose it with their cursor, and
  // the card is the last chance to notice it is not where they meant.
  const anchor = proposal.anchorText?.trim() ?? "";
  const anchorPreview = anchor.length > 60 ? `${anchor.slice(0, 59)}…` : anchor;
  const placement = isRedraft ? "" : anchorPreview ? `, after "${anchorPreview}"` : ", at the end of the section";

  // While applying an approve, replace the card with the stepped checklist
  // so the writer sees the 15–30s Word render progress. Reject keeps the
  // plain busy card (applySteps is empty there).
  if (phase === "applying" && applySteps.length > 0) {
    return (
      <ApplyProgress
        steps={applySteps}
        chipText={chip}
        header={`Applying the ${noun} to ${sectionLabel}…`}
        ariaLabel={`Applying ${noun} to ${sectionLabel}`}
      />
    );
  }

  // Preview exactly what Approve will apply — the approve path runs the
  // same sanitizer before submitting to the SSOT + Word.
  const previewMarkdown = useMemo(() => sanitizeDraftForWord(proposal.content), [proposal.content]);

  return (
    <AwaitingDecisionFrame show={phase === "pending_decision"}>
      <ProposalCard
        chipText={chip}
        header={`Proposed ${noun} to ${proposal.sectionLabel}${placement} — "${instructionPreview}"`}
        previewMarkdown={previewMarkdown}
        diffBefore={isRedraft && proposal.originalText ? sanitizeDraftForWord(proposal.originalText) : null}
        warning={driftWarning}
        metaChips={[proposal.sectionLabel, `${proposal.wordCount} words`]}
        busy={phase === "applying"}
        approveLabel={driftWarning ? "Approve & overwrite" : isRedraft ? "Approve & replace" : "Approve & insert"}
        busyLabel="Applying…"
        ariaLabel={`Section ${noun} proposal for ${proposal.sectionLabel}`}
        onApprove={onApprove}
        onReject={onReject}
      />
    </AwaitingDecisionFrame>
  );
};

export default AdhocProposalMessage;
